<template>
  <v-card class="sw-modal">
    <div class="sw-modal-head">
      <v-icon size="20" start color="white">mdi-phone-plus</v-icon>
      Nouveau ticket
    </div>

    <div class="sw-modal-body">
      <!-- Client : recherche dans le layout « Clients » -->
      <v-autocomplete
        v-model="picked"
        :items="clients"
        item-title="societe"
        item-value="recordId"
        return-object
        :custom-filter="clientFilter"
        :loading="isClientsLoading"
        label="Client appelant (nom, code ou téléphone)"
        prepend-inner-icon="mdi-account-search-outline"
        variant="outlined"
        density="comfortable"
        hide-details
        clearable
        autofocus
        no-data-text="Aucun client trouvé — saisissez la société ci-dessous"
        @update:modelValue="onPicked"
      >
        <template #item="{ props, item }">
          <v-list-item v-bind="props" :title="item.raw.societe" :subtitle="[item.raw.code, item.raw.telephone].filter(Boolean).join(' · ')">
            <template #append>
              <span v-if="item.raw.repondre" class="sw-yn sm" :class="isYes(item.raw.repondre) ? 'ok' : 'ko'">Tél. {{ yesNo(item.raw.repondre) }}</span>
            </template>
          </v-list-item>
        </template>
      </v-autocomplete>

      <div v-if="!isClientsLoading && !clients.length" class="sw-modal-hint">
        <v-icon size="16" color="#FB8C00">mdi-alert-outline</v-icon>
        Liste des clients indisponible : saisissez la société manuellement.
        <a href="#" @click.prevent="$emit('reloadClients')">Recharger</a>
      </div>

      <div v-if="picked && (picked.repondre || picked.heberge)" class="sw-modal-flags">
        <span v-if="picked.repondre" class="sw-yn" :class="isYes(picked.repondre) ? 'ok' : 'ko'">Répondre au téléphone : {{ yesNo(picked.repondre) }}</span>
        <span v-if="picked.heberge" class="sw-yn" :class="isYes(picked.heberge) ? 'ok' : 'ko'">Client hébergé : {{ yesNo(picked.heberge) }}</span>
      </div>

      <div class="sw-grid2 mt-3">
        <v-text-field v-model="form.societe" label="Société *" variant="outlined" density="compact" hide-details></v-text-field>
        <v-text-field v-model="form.client" label="Code client" variant="outlined" density="compact" hide-details></v-text-field>
        <v-text-field v-model="form.telephone" label="Téléphone" prepend-inner-icon="mdi-phone-outline" variant="outlined" density="compact" hide-details></v-text-field>
        <v-combobox v-model="form.forme" :items="formeOptions" label="Forme" variant="outlined" density="compact" hide-details></v-combobox>
        <v-select v-model="form.statut" :items="statutOptions" label="Statut" variant="outlined" density="compact" hide-details></v-select>
        <v-combobox v-model="form.user_interne" :items="userOptions" label="Pris en charge par" prepend-inner-icon="mdi-account-outline" variant="outlined" density="compact" hide-details></v-combobox>
      </div>

      <v-textarea
        ref="commentaire"
        v-model="form.commentaire"
        label="Demande / motif de l'appel *"
        variant="outlined"
        density="compact"
        hide-details
        auto-grow
        rows="4"
        max-rows="12"
        class="mt-3"
        @keydown="onKey"
      ></v-textarea>
    </div>

    <div class="sw-modal-foot">
      <span class="sw-modal-req">* obligatoire</span>
      <v-spacer></v-spacer>
      <v-btn variant="text" color="#607D8B" :disabled="isSaving" @click="$emit('close')">Annuler</v-btn>
      <v-btn variant="flat" color="#5C6BC0" style="color:white" :disabled="!canCreate" :loading="isSaving" @click="submit">
        <v-icon start>mdi-check</v-icon> Créer le ticket
      </v-btn>
    </div>
  </v-card>
</template>

<script>
const norm = (s) => (s === undefined || s === null ? "" : String(s)).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
const digits = (s) => (s === undefined || s === null ? "" : String(s)).replace(/\D/g, "");

export default {
  props: {
    clients: { type: Array, default: () => [] },
    isClientsLoading: { type: Boolean, default: false },
    formeOptions: { type: Array, default: () => [] },
    statutOptions: { type: Array, default: () => [] },
    userOptions: { type: Array, default: () => [] },
    currentUser: { type: String, default: "" },
    isSaving: { type: Boolean, default: false },
  },
  emits: ["close", "create", "reloadClients"],
  data() {
    return {
      picked: null,
      form: {
        societe: "",
        client: "",
        telephone: "",
        forme: this.formeOptions[0] || "",
        statut: this.statutOptions[0] || "",
        user_interne: this.currentUser || "",
        commentaire: "",
      },
    };
  },
  computed: {
    canCreate() {
      const f = this.form;
      return !this.isSaving && !!(String(f.societe || "").trim() || String(f.client || "").trim()) && !!String(f.commentaire || "").trim();
    },
  },
  methods: {
    isYes(v) {
      return /^(oui|o|yes|y|1|vrai|true|x)$/.test(norm(v));
    },
    // Les champs FileMaker valent souvent 1/0 → affichage OUI/NON.
    yesNo(v) {
      return this.isYes(v) ? "OUI" : "NON";
    },
    // Recherche sur la raison sociale, le code client et le téléphone.
    clientFilter(value, query, item) {
      const q = norm(query);
      if (!q) return true;
      const c = (item && item.raw) || {};
      if ([c.societe, c.code, c.telephone].some((v) => norm(v).indexOf(q) !== -1)) return true;
      const qd = digits(query);
      return qd.length >= 3 && digits(c.telephone).indexOf(qd) !== -1;
    },
    onPicked(c) {
      if (!c) return;
      this.form.societe = c.societe || "";
      this.form.client = c.code || "";
      if (c.telephone) this.form.telephone = c.telephone;
      this.$nextTick(() => {
        const ta = this.$refs.commentaire;
        if (ta && ta.focus) ta.focus();
      });
    },
    onKey(e) {
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && this.canCreate) {
        e.preventDefault();
        this.submit();
      }
    },
    submit() {
      if (!this.canCreate) return;
      this.$emit("create", Object.assign({}, this.form));
    },
  },
};
</script>
